import React, { useEffect, useState, Children } from 'react';
import { Badge } from '../components/ui/Badge';
export function LegalPage() {
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms'>('privacy');
  // Allow deep-linking to #terms
  useEffect(() => {
    if (window.location.hash === '#terms') {
      setActiveTab('terms');
    }
  }, []);
  const effectiveDate = 'March 1, 2026';
  const companyName = 'Slate & Page';
  const contactEmail = 'hello@slateandpage.com';
  const website = 'slateandpage.app';
  return <main className="w-full bg-cream min-h-screen" data-id="element-910">
      <section className="pt-32 pb-16 px-6 lg:px-8" data-id="element-911">
        <div className="max-w-4xl mx-auto" data-id="element-912">
          <Badge variant="amber" className="mb-6" data-id="element-913">
            Legal
          </Badge>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-ink mb-4 leading-tight" data-id="element-914">
            Privacy & Terms
          </h1>
          <p className="font-sans text-base text-ink-muted mb-10" data-id="element-915">
            Effective date: {effectiveDate}
          </p>

          {/* Tab Toggle */}
          <div className="inline-flex items-center bg-parchment rounded-full p-1.5 border border-parchment mb-12" data-id="element-916">
            <button className={`font-sans text-sm px-6 py-2.5 rounded-full transition-all ${activeTab === 'privacy' ? 'bg-white text-ink shadow-sm font-medium' : 'text-ink-muted hover:text-ink'}`} onClick={() => setActiveTab('privacy')} aria-pressed={activeTab === 'privacy'} data-id="element-917">
              
              Privacy Policy
            </button>
            <button id="terms" className={`font-sans text-sm px-6 py-2.5 rounded-full transition-all ${activeTab === 'terms' ? 'bg-white text-ink shadow-sm font-medium' : 'text-ink-muted hover:text-ink'}`} onClick={() => setActiveTab('terms')} aria-pressed={activeTab === 'terms'} data-id="element-918">
              
              Terms of Service
            </button>
          </div>

          {/* Privacy Policy */}
          {activeTab === 'privacy' && <div className="bg-white rounded-2xl border border-parchment shadow-sm p-8 md:p-12 prose-legal" data-id="element-919">
              <h2 className="font-serif text-2xl font-bold text-ink mb-2" data-id="element-920">
                Privacy Policy
              </h2>
              <p className="font-sans text-xs text-ink-light mb-8" data-id="element-921">
                Last updated: {effectiveDate}
              </p>

              <LegalSection title="1. Introduction" data-id="element-922">
                <p data-id="element-923">
                  {companyName} ("we," "our," or "us") is committed to
                  protecting your personal information. This Privacy Policy
                  explains how we collect, use, disclose, and safeguard your
                  information when you use our website and services at {website}{' '}
                  (the "Service"). Please read this policy carefully. If you
                  disagree with its terms, please discontinue use of the
                  Service.
                </p>
              </LegalSection>

              <LegalSection title="2. Information We Collect" data-id="element-924">
                <p className="mb-3" data-id="element-925">
                  We may collect the following types of information:
                </p>
                <ul data-id="element-926">
                  <li data-id="element-927">
                    <strong data-id="element-928">Account information:</strong> Name, email address,
                    and password when you create an account or join our
                    waitlist.
                  </li>
                  <li data-id="element-929">
                    <strong data-id="element-930">Profile information:</strong> Optional details such
                    as company or studio name.
                  </li>
                  <li data-id="element-931">
                    <strong data-id="element-932">Usage data:</strong> Information about how you
                    interact with the Service, including pages visited, features
                    used, and time spent.
                  </li>
                  <li data-id="element-933">
                    <strong data-id="element-934">Device information:</strong> Browser type, operating
                    system, IP address, and other technical identifiers.
                  </li>
                  <li data-id="element-935">
                    <strong data-id="element-936">Content data:</strong> Story elements, characters,
                    scenes, locations, and other content you create within the
                    Service.
                  </li>
                  <li data-id="element-937">
                    <strong data-id="element-938">Communications:</strong> Messages you send us via
                    email or support channels.
                  </li>
                </ul>
              </LegalSection>

              <LegalSection title="3. How We Use Your Information" data-id="element-939">
                <p className="mb-3" data-id="element-940">We use the information we collect to:</p>
                <ul data-id="element-941">
                  <li data-id="element-942">Provide, operate, and improve the Service</li>
                  <li data-id="element-943">Create and manage your account</li>
                  <li data-id="element-944">
                    Send transactional emails (account confirmations, password
                    resets)
                  </li>
                  <li data-id="element-945">
                    Send product updates and announcements (you may opt out at
                    any time)
                  </li>
                  <li data-id="element-946">Respond to your questions and support requests</li>
                  <li data-id="element-947">
                    Monitor and analyze usage patterns to improve the Service
                  </li>
                  <li data-id="element-948">
                    Detect and prevent fraud, abuse, and security incidents
                  </li>
                  <li data-id="element-949">Comply with legal obligations</li>
                </ul>
              </LegalSection>

              <LegalSection title="4. Your Content" data-id="element-950">
                <p data-id="element-951">
                  Your story content — characters, scenes, locations,
                  world-building elements, and all other creative material you
                  enter into the Service — belongs to you. We do not claim
                  ownership of your content, and we will never use your story
                  content to train AI models or share it with third parties
                  without your explicit consent.
                </p>
                <p className="mt-3" data-id="element-952">
                  We store your content securely to provide the Service. You may
                  export or delete your content at any time.
                </p>
              </LegalSection>

              <LegalSection title="5. Sharing of Information" data-id="element-953">
                <p className="mb-3" data-id="element-954">
                  We do not sell your personal information. We may share
                  information in the following limited circumstances:
                </p>
                <ul data-id="element-955">
                  <li data-id="element-956">
                    <strong data-id="element-957">Service providers:</strong> Third-party vendors who
                    assist us in operating the Service (e.g., hosting,
                    analytics, email delivery), subject to confidentiality
                    obligations.
                  </li>
                  <li data-id="element-958">
                    <strong data-id="element-959">Legal requirements:</strong> When required by law,
                    court order, or governmental authority.
                  </li>
                  <li data-id="element-960">
                    <strong data-id="element-961">Business transfers:</strong> In connection with a
                    merger, acquisition, or sale of assets, with notice to
                    affected users.
                  </li>
                  <li data-id="element-962">
                    <strong data-id="element-963">With your consent:</strong> In any other
                    circumstances with your explicit permission.
                  </li>
                </ul>
              </LegalSection>

              <LegalSection title="6. Data Retention" data-id="element-964">
                <p data-id="element-965">
                  We retain your personal information for as long as your
                  account is active or as needed to provide the Service. If you
                  delete your account, we will delete or anonymize your personal
                  information within 30 days, except where retention is required
                  by law.
                </p>
              </LegalSection>

              <LegalSection title="7. Security" data-id="element-966">
                <p data-id="element-967">
                  We implement industry-standard security measures to protect
                  your information, including encryption in transit (TLS) and at
                  rest. However, no method of transmission over the internet is
                  100% secure, and we cannot guarantee absolute security.
                </p>
              </LegalSection>

              <LegalSection title="8. Your Rights" data-id="element-968">
                <p className="mb-3" data-id="element-969">
                  Depending on your location, you may have the right to:
                </p>
                <ul data-id="element-970">
                  <li data-id="element-971">Access the personal information we hold about you</li>
                  <li data-id="element-972">Correct inaccurate or incomplete information</li>
                  <li data-id="element-973">Request deletion of your personal information</li>
                  <li data-id="element-974">Object to or restrict certain processing</li>
                  <li data-id="element-975">
                    Data portability (receive your data in a machine-readable
                    format)
                  </li>
                  <li data-id="element-976">
                    Withdraw consent at any time where processing is based on
                    consent
                  </li>
                </ul>
                <p className="mt-3" data-id="element-977">
                  To exercise these rights, contact us at{' '}
                  <a href={`mailto:${contactEmail}`} className="text-amber hover:text-amber-dark" data-id="element-978">
                  
                    {contactEmail}
                  </a>
                  .
                </p>
              </LegalSection>

              <LegalSection title="9. Cookies" data-id="element-979">
                <p data-id="element-980">
                  We use cookies and similar tracking technologies to operate
                  the Service, remember your preferences, and analyze usage. You
                  can control cookies through your browser settings, though
                  disabling certain cookies may affect Service functionality.
                </p>
              </LegalSection>

              <LegalSection title="10. Children's Privacy" data-id="element-981">
                <p data-id="element-982">
                  The Service is not directed to individuals under the age of
                  13. We do not knowingly collect personal information from
                  children under 13. If we become aware that a child under 13
                  has provided us with personal information, we will delete it
                  promptly.
                </p>
              </LegalSection>

              <LegalSection title="11. Changes to This Policy" data-id="element-983">
                <p data-id="element-984">
                  We may update this Privacy Policy from time to time. We will
                  notify you of material changes by posting the new policy on
                  this page and updating the effective date. Your continued use
                  of the Service after changes constitutes acceptance of the
                  updated policy.
                </p>
              </LegalSection>

              <LegalSection title="12. Contact Us" isLast data-id="element-985">
                <p data-id="element-986">
                  If you have questions about this Privacy Policy, please
                  contact us at:
                  <br data-id="element-987" />
                  <a href={`mailto:${contactEmail}`} className="text-amber hover:text-amber-dark" data-id="element-988">
                  
                    {contactEmail}
                  </a>
                </p>
              </LegalSection>
            </div>}

          {/* Terms of Service */}
          {activeTab === 'terms' && <div className="bg-white rounded-2xl border border-parchment shadow-sm p-8 md:p-12" data-id="element-989">
              <h2 className="font-serif text-2xl font-bold text-ink mb-2" data-id="element-990">
                Terms of Service
              </h2>
              <p className="font-sans text-xs text-ink-light mb-8" data-id="element-991">
                Last updated: {effectiveDate}
              </p>

              <LegalSection title="1. Acceptance of Terms" data-id="element-992">
                <p data-id="element-993">
                  By accessing or using {companyName} at {website} (the
                  "Service"), you agree to be bound by these Terms of Service
                  ("Terms"). If you do not agree to these Terms, do not use the
                  Service. These Terms apply to all users, including visitors,
                  registered users, and paying subscribers.
                </p>
              </LegalSection>

              <LegalSection title="2. Description of Service" data-id="element-994">
                <p data-id="element-995">
                  {companyName} is a story development and world-building
                  platform designed for screenwriters. The Service allows users
                  to create and manage character profiles, scene plans, location
                  notes, and world-building elements for screenplay projects.
                  Features vary by subscription plan.
                </p>
              </LegalSection>

              <LegalSection title="3. Accounts" data-id="element-996">
                <p className="mb-3" data-id="element-997">
                  To access certain features, you must create an account. You
                  agree to:
                </p>
                <ul data-id="element-998">
                  <li data-id="element-999">
                    Provide accurate and complete registration information
                  </li>
                  <li data-id="element-1000">Maintain the security of your password and account</li>
                  <li data-id="element-1001">
                    Notify us immediately of any unauthorized account access
                  </li>
                  <li data-id="element-1002">
                    Accept responsibility for all activity under your account
                  </li>
                </ul>
                <p className="mt-3" data-id="element-1003">
                  You must be at least 13 years old to create an account. We
                  reserve the right to suspend or terminate accounts that
                  violate these Terms.
                </p>
              </LegalSection>

              <LegalSection title="4. Your Content" data-id="element-1004">
                <p data-id="element-1005">
                  You retain full ownership of all content you create within the
                  Service, including characters, scenes, locations, and story
                  elements ("Your Content"). By using the Service, you grant us
                  a limited, non-exclusive license to store, display, and
                  process Your Content solely to provide the Service to you.
                </p>
                <p className="mt-3" data-id="element-1006">
                  You are responsible for ensuring Your Content does not violate
                  any third-party rights or applicable laws. We will never use
                  Your Content to train AI models or share it with third parties
                  without your explicit consent.
                </p>
              </LegalSection>

              <LegalSection title="5. Acceptable Use" data-id="element-1007">
                <p className="mb-3" data-id="element-1008">You agree not to use the Service to:</p>
                <ul data-id="element-1009">
                  <li data-id="element-1010">Violate any applicable laws or regulations</li>
                  <li data-id="element-1011">Infringe the intellectual property rights of others</li>
                  <li data-id="element-1012">
                    Upload or transmit malicious code, viruses, or harmful
                    content
                  </li>
                  <li data-id="element-1013">
                    Attempt to gain unauthorized access to the Service or other
                    users' accounts
                  </li>
                  <li data-id="element-1014">
                    Scrape, crawl, or systematically extract data from the
                    Service
                  </li>
                  <li data-id="element-1015">
                    Use the Service for any unlawful or fraudulent purpose
                  </li>
                  <li data-id="element-1016">Harass, abuse, or harm other users</li>
                </ul>
              </LegalSection>

              <LegalSection title="6. Subscription Plans and Billing" data-id="element-1017">
                <p data-id="element-1018">
                  The Service offers free and paid subscription plans. Paid
                  plans are billed in advance on a monthly or annual basis. All
                  fees are non-refundable except as required by law or as
                  expressly stated in these Terms.
                </p>
                <p className="mt-3" data-id="element-1019">
                  We reserve the right to change pricing with reasonable notice.
                  Continued use of a paid plan after a price change constitutes
                  acceptance of the new pricing. You may cancel your
                  subscription at any time; cancellation takes effect at the end
                  of the current billing period.
                </p>
              </LegalSection>

              <LegalSection title="7. Free Plan Limitations" data-id="element-1020">
                <p data-id="element-1021">
                  The free plan is subject to usage limits (including limits on
                  projects, characters, scenes, and locations) as described on
                  our Pricing page. We reserve the right to modify free plan
                  limits with reasonable notice.
                </p>
              </LegalSection>

              <LegalSection title="8. Intellectual Property" data-id="element-1022">
                <p data-id="element-1023">
                  The Service, including its design, code, features, and
                  branding, is owned by {companyName} and protected by
                  applicable intellectual property laws. You may not copy,
                  modify, distribute, or create derivative works based on the
                  Service without our express written permission.
                </p>
              </LegalSection>

              <LegalSection title="9. Disclaimer of Warranties" data-id="element-1024">
                <p data-id="element-1025">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
                  WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                  PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
                  SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY
                  SECURE.
                </p>
              </LegalSection>

              <LegalSection title="10. Limitation of Liability" data-id="element-1026">
                <p data-id="element-1027">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW,{' '}
                  {companyName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                  DAMAGES ARISING FROM YOUR USE OF THE SERVICE, EVEN IF WE HAVE
                  BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL
                  LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE
                  TWELVE MONTHS PRECEDING THE CLAIM.
                </p>
              </LegalSection>

              <LegalSection title="11. Termination" data-id="element-1028">
                <p data-id="element-1029">
                  We may suspend or terminate your access to the Service at any
                  time for violation of these Terms, with or without notice. You
                  may terminate your account at any time by contacting us or
                  through your account settings. Upon termination, your right to
                  use the Service ceases immediately. We will provide a
                  reasonable opportunity to export your content before account
                  deletion.
                </p>
              </LegalSection>

              <LegalSection title="12. Governing Law" data-id="element-1030">
                <p data-id="element-1031">
                  These Terms are governed by and construed in accordance with
                  applicable law. Any disputes arising from these Terms or your
                  use of the Service shall be resolved through binding
                  arbitration or in the courts of competent jurisdiction, as
                  applicable.
                </p>
              </LegalSection>

              <LegalSection title="13. Changes to Terms" data-id="element-1032">
                <p data-id="element-1033">
                  We may update these Terms from time to time. We will notify
                  you of material changes by email or by posting a notice on the
                  Service. Your continued use of the Service after changes
                  constitutes acceptance of the updated Terms.
                </p>
              </LegalSection>

              <LegalSection title="14. Contact Us" isLast data-id="element-1034">
                <p data-id="element-1035">
                  If you have questions about these Terms, please contact us at:
                  <br data-id="element-1036" />
                  <a href={`mailto:${contactEmail}`} className="text-amber hover:text-amber-dark" data-id="element-1037">
                  
                    {contactEmail}
                  </a>
                </p>
              </LegalSection>
            </div>}
        </div>
      </section>
    </main>;
}
function LegalSection({
  title,
  children,
  isLast = false
}: {
  title: string;
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return <div className={`${isLast ? '' : 'mb-8 pb-8 border-b border-parchment'}`} data-id="element-1038">
      <h3 className="font-serif text-lg font-semibold text-ink mb-3" data-id="element-1039">
        {title}
      </h3>
      <div className="font-sans text-sm text-ink-muted leading-relaxed space-y-2" data-id="element-1040">
        {children}
      </div>
    </div>;
}